/**
 * Step 9+10: hook fetching /api/ux-predict.
 * Timeout 150ms, cache 30s per session.
 * Fallback: { dropoffRisk: 0, nextBestAction: "none", isActive: false }
 */

import { useState, useEffect, useRef } from "react";
import { getUxFlags } from "@/lib/uxFlags";
import { UX_SESSION_STORAGE_KEY } from "@/lib/uxTracking";

const FETCH_TIMEOUT_MS = 150;
const CACHE_TTL_MS = 30_000;

export type NextBestAction =
  | "none"
  | "nudge_whatsapp_soft"
  | "nudge_form_soft"
  | "nudge_access_soft"
  | "handover_human";

export interface PredictiveUxResult {
  dropoffRisk: number;
  nextBestAction: NextBestAction;
  isActive: boolean;
}

interface PredictCacheEntry {
  data: PredictiveUxResult;
  expiresAt: number;
}

let memoryCache: { sessionId: string; entry: PredictCacheEntry } | null = null;

function getCached(sessionId: string): PredictiveUxResult | null {
  if (!memoryCache || memoryCache.sessionId !== sessionId || Date.now() >= memoryCache.entry.expiresAt) {
    return null;
  }
  return memoryCache.entry.data;
}

function setCached(sessionId: string, data: PredictiveUxResult): void {
  memoryCache = { sessionId, entry: { data, expiresAt: Date.now() + CACHE_TTL_MS } };
}

const FALLBACK: PredictiveUxResult = {
  dropoffRisk: 0,
  nextBestAction: "none",
  isActive: false,
};

export function usePredictiveUx(): PredictiveUxResult {
  const [result, setResult] = useState<PredictiveUxResult>(FALLBACK);
  const fetched = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const sessionId =
      typeof sessionStorage !== "undefined" ? sessionStorage.getItem(UX_SESSION_STORAGE_KEY) : null;

    const run = async () => {
      const flags = await getUxFlags();
      if (!flags.adaptiveUxEnabled || !flags.uxPredictEnabled || !sessionId) {
        return;
      }

      const cached = getCached(sessionId);
      if (cached) {
        if (!cancelled) setResult(cached);
        return;
      }

      const ac = new AbortController();
      const timeoutId = setTimeout(() => ac.abort(), FETCH_TIMEOUT_MS);

      try {
        const r = await fetch(`/api/ux-predict?session_id=${encodeURIComponent(sessionId)}`, {
          credentials: "same-origin",
          signal: ac.signal,
        });
        clearTimeout(timeoutId);
        if (cancelled) return;
        if (r.status !== 200) return; // 204 or error → keep FALLBACK
        const data = (await r.json()) as { dropoffRisk: number; nextBestAction: NextBestAction };
        const next: PredictiveUxResult = {
          dropoffRisk: typeof data.dropoffRisk === "number" ? data.dropoffRisk : 0,
          nextBestAction: data.nextBestAction ?? "none",
          isActive: true,
        };
        setCached(sessionId, next);
        if (!cancelled) setResult(next);
      } catch {
        clearTimeout(timeoutId);
        // Timeout or network error → stay on FALLBACK silently
      }
    };

    if (!fetched.current) {
      fetched.current = true;
      void run();
    }

    return () => { cancelled = true; };
  }, []);

  return result;
}
